import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ProductCard from '../components/ProductCard';
import { useScrollReveal } from '../hooks/useScrollReveal';
import type { Product } from '../types';

const categories = [
  { value: 'all', label: 'All' },
  { value: 'jeans', label: 'Jeans' },
  { value: 'shoes', label: 'Shoes' },
  { value: 'polos', label: 'Polos' },
  { value: 'sweaters', label: 'Sweaters' },
  { value: 'trousers', label: 'Trousers' },
  { value: 'jackets', label: 'Jackets' },
  { value: 'accessories', label: 'Accessories' },
];

type SortOption = 'newest' | 'price-asc' | 'price-desc' | 'name';

const Category = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const containerRef = useScrollReveal();

  const activeSlug = categorySlug || 'all';
  const currentCategory = categories.find(c => c.value === activeSlug) || categories[0];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = '/api/products?limit=50';
        if (activeSlug !== 'all') {
          url += `&category=${activeSlug}`;
        }
        const res = await fetch(url);
        const data = await res.json();
        if (data.success) {
          setProducts(data.data.products.filter((p: Product) => p.isActive));
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeSlug]);

  const sorted = useMemo(() => {
    const copy = [...products];
    switch (sortBy) {
      case 'price-asc':
        return copy.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
      case 'price-desc':
        return copy.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
      case 'name':
        return copy.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return copy;
    }
  }, [products, sortBy]);

  return (
    <div ref={containerRef} className="min-h-screen bg-offwhite">
      {/* Page Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="section-wide">
          <p className="text-xs tracking-[0.2em] uppercase text-warmgray-500 mb-3 reveal">
            Shop / {currentCategory.label}
          </p>
          <h1 className="font-display text-display-lg tracking-tight text-offblack reveal">
            {currentCategory.label === 'All' ? 'All Products' : currentCategory.label}
          </h1>
        </div>
      </section>

      {/* Category Pills + Sort */}
      <section className="border-y border-warmgray-200 bg-white sticky top-[72px] z-30">
        <div className="section-wide py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Category Pills */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.value}
                  to={`/category/${cat.value}`}
                  className={`shrink-0 px-4 py-2 text-sm tracking-wide border transition-all duration-200 ${
                    activeSlug === cat.value
                      ? 'bg-offblack text-offwhite border-offblack'
                      : 'bg-transparent text-warmgray-600 border-warmgray-300 hover:border-offblack hover:text-offblack'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>

            {/* Sort + Count */}
            <div className="flex items-center gap-4 shrink-0">
              <span className="text-sm text-warmgray-500">
                {sorted.length} {sorted.length === 1 ? 'product' : 'products'}
              </span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none bg-transparent border border-warmgray-300 pl-3 pr-8 py-2 text-sm text-offblack focus:outline-none focus:border-offblack cursor-pointer"
                >
                  <option value="newest">Newest</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name">Name A–Z</option>
                </select>
                <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-warmgray-500 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-12 md:py-16">
        <div className="section-wide">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="spinner" />
            </div>
          ) : sorted.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {sorted.map((product) => (
                <div key={product._id} className="reveal">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <p className="font-display text-display-sm text-warmgray-400 mb-4">
                No products found
              </p>
              <p className="text-warmgray-500 mb-8 max-w-md mx-auto">
                We don't have any {currentCategory.label.toLowerCase()} right now. Check back soon or explore other categories.
              </p>
              <Link to="/category/all" className="btn-primary">
                View All Products
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Category;
